/**
 * Get the filtered category tree based on the active category.
 *
 * Tier 1 and Tier 2 categories are most likely to have many sibling categories.
 * For these categories, we prioritize subcategories instead of siblings to avoid cluttering the UI.
 * For deeper tiers, we assume sibling categories are more relevant, so we prioritize them instead subcategories.
 *
 * @example
 * Tier 1: /mens (subcategories)
 * Tier 2: /mens/tops (subcategories)
 * Tier 3: /mens/tops/sweatshirts (siblings)
 * Tier 4: /mens/tops/sweatshirts/hoodies (siblings)
 *
 * @param {Object} activeCategory - The currently active category object, which may contain
 *                                  subcategories, siblings, and ancestors.
 * @returns {Object} ret.ancestor - The ancestor category object.
 * @returns {Array} ret.categoryTree - The filtered list of subcategories or siblings.
 * @returns {number} ret.categoryTier - The tier level of the active category.
 * @returns {boolean} ret.isUsingSubcategoriesAsRoot - Indicates if subcategories are being used at the root level of the tree.
 * @returns {boolean} ret.isUsingSiblingsAsRoot - Indicates if siblings are being used at the root level of the tree.
 */
export const filterCategoryTree = (activeCategory) => {
  const ancestor = activeCategory?.ancestors?.[0] || activeCategory;

  const categoryTier =
    activeCategory?.urlPath?.split('/')?.filter(Boolean)?.length || 0;

  const shouldPrioritizeSubcategories = categoryTier < 3;

  let baseTree = [];
  let isUsingSubcategoriesAsRoot = false;
  let isUsingSiblingsAsRoot = false;

  // Prioritize subcategories or siblings based on the category tier
  // Use the alternative version if the prioritized field is empty
  if (shouldPrioritizeSubcategories) {
    // Prioritize subcategories then siblings
    if (activeCategory?.subcategories?.length > 0) {
      baseTree = activeCategory.subcategories;
      isUsingSubcategoriesAsRoot = true;
    } else if (activeCategory?.siblings?.length > 0) {
      baseTree = activeCategory.siblings;
      isUsingSiblingsAsRoot = true;
    }
  } else {
    // Prioritize siblings then subcategories
    if (activeCategory?.siblings?.length > 0) {
      baseTree = activeCategory.siblings;
      isUsingSiblingsAsRoot = true;
    } else if (activeCategory?.subcategories?.length > 0) {
      baseTree = activeCategory.subcategories;
      isUsingSubcategoriesAsRoot = true;
    }
  }

  // Remove potential duplicate ancestor from the base tree
  const filteredTree = baseTree?.filter(
    (category) => category?.urlPath !== ancestor?.urlPath
  );

  return {
    ancestor,
    categoryTree: filteredTree,
    categoryTier,
    isUsingSubcategoriesAsRoot,
    isUsingSiblingsAsRoot,
  };
};
